/**
 * 🎯 TUTORIAL ARREGLADO - Versión sin dependencias circulares
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaArrowLeft, FaArrowRight, FaLightbulb, FaRocket, FaUsers, FaChartBar, FaFileExport, FaCog, FaList } from 'react-icons/fa';

// Añadir estilos CSS para las animaciones
const tutorialStyles = `
  @keyframes tutorialPulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.02);
    }
  }
  
  .tutorial-highlight-ring {
    animation: tutorialPulse 2s infinite ease-in-out;
  }
`;

// Inyectar estilos
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = tutorialStyles;
  if (!document.head.querySelector('style[data-tutorial="true"]')) {
    styleElement.setAttribute('data-tutorial', 'true');
    document.head.appendChild(styleElement);
  }
}

export type TutorialSection = 'dashboard' | 'formularios' | 'analytics' | 'exportacion';

interface TutorialStep {
  id: string;
  title: string;
  content: string;
  targetSelector?: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: 'highlight' | 'click' | 'wait' | 'navigate' | 'selectDimension' | 'changeView' | 'dimension-select' | 'view-mode-change' | 'student-select';
  actionTarget?: string | number | 'student' | 'skill';
  expectedSection: TutorialSection;
  icon?: any;
  waitTime?: number;
}

interface TutorialState {
  isActive: boolean;
  currentStep: number;
  currentSection: TutorialSection;
}

interface TutorialProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
  currentSection: TutorialSection;
  onSectionChange?: (section: TutorialSection) => void;
  onDimensionSelect?: (dimensionId: number) => void;
  onViewModeChange?: (mode: 'student' | 'skill') => void;
}

// Iconos por paso expandidos
const stepIcons: Record<string, any> = {
  // Dashboard
  'welcome': <FaRocket className="text-2xl text-blue-500" />,
  'navigation-menu': <FaCog className="text-xl text-purple-500" />,
  'formularios-access': <FaUsers className="text-xl text-blue-500" />,
  
  // Formularios - Vista General
  'dimension-categories-overview': <FaChartBar className="text-xl text-indigo-500" />,
  'select-dimension': <FaChartBar className="text-xl text-green-500" />,
  
  // Formularios - Dentro de Dimensión
  'dimension-interface': <FaUsers className="text-xl text-blue-600" />,
  'view-mode-selector': <FaCog className="text-xl text-purple-600" />,
  'select-student-view': <FaUsers className="text-xl text-blue-600" />,
  'student-selection': <FaUsers className="text-xl text-green-500" />,
  'evaluation-area': <FaChartBar className="text-xl text-indigo-600" />,
  'save-system-explanation': <FaLightbulb className="text-xl text-yellow-500" />,
  'switch-to-skill-view': <FaList className="text-xl text-purple-600" />,
  'skill-evaluation-interface': <FaList className="text-xl text-indigo-500" />,
  'save-progress-note': <FaLightbulb className="text-xl text-yellow-600" />,
  
  // Analytics
  'analytics-access': <FaChartBar className="text-xl text-orange-500" />,
  'analytics-overview': <FaChartBar className="text-xl text-blue-500" />,
  
  // Exportación
  'export-access': <FaFileExport className="text-xl text-red-500" />,
  'export-options-overview': <FaFileExport className="text-xl text-purple-500" />,
  'students-list-export': <FaUsers className="text-xl text-red-400" />,
  'select-student-for-pdf': <FaUsers className="text-xl text-red-500" />,
  'student-individual-view': <FaFileExport className="text-xl text-blue-400" />,
  'generate-pdf-button': <FaFileExport className="text-xl text-red-600" />,
  
  // Finalización
  'completion': <FaLightbulb className="text-2xl text-yellow-500" />
};

// Tutorial expandido con pasos detallados
const tutorialSteps: TutorialStep[] = [
  // === SECCIÓN DASHBOARD ===
  {
    id: 'welcome',
    title: '¡Bienvenido a la Plataforma!',
    content: 'Esta plataforma te permite realizar seguimiento integral del desarrollo de habilidades de los estudiantes. Te guiaremos paso a paso por todas las funcionalidades.',
    targetSelector: '.dashboard-stats',
    position: 'center',
    action: 'highlight',
    expectedSection: 'dashboard'
  },
  {
    id: 'navigation-menu', 
    title: 'Menú de Navegación',
    content: 'Utiliza este menú para navegar entre las diferentes secciones: Dashboard, Formularios, Analytics y Exportación.',
    targetSelector: '[data-section="dashboard"]',
    position: 'bottom',
    action: 'highlight',
    expectedSection: 'dashboard'
  },
  {
    id: 'formularios-access',
    title: 'Acceso a Formularios', 
    content: 'Los formularios son el corazón de la plataforma donde evaluarás a los estudiantes. Te llevaremos automáticamente a esta sección.',
    targetSelector: '[data-section="formularios"]',
    position: 'bottom',
    action: 'click',
    actionTarget: 'formularios',
    expectedSection: 'dashboard'
  },

  // === SECCIÓN FORMULARIOS - VISTA GENERAL ===
  {
    id: 'dimension-categories-overview',
    title: 'Categorías de Dimensiones',
    content: 'Aquí puedes ver todas las dimensiones de evaluación organizadas por categorías. Cada dimensión agrupa habilidades específicas que puedes evaluar.',
    targetSelector: '.dimension-categories',
    position: 'top',
    action: 'highlight',
    expectedSection: 'formularios'
  },
  {
    id: 'select-dimension',
    title: 'Selecciona una Dimensión',
    content: 'Para evaluar estudiantes, primero debes seleccionar una dimensión específica. El tutorial seleccionará automáticamente la primera dimensión disponible.',
    targetSelector: '.space-y-1 button:first-child',
    position: 'right',
    action: 'dimension-select',
    actionTarget: 1, // Selecciona la dimensión ID 1
    expectedSection: 'formularios'
  },

  // === FORMULARIOS - DENTRO DE UNA DIMENSIÓN ===
  {
    id: 'dimension-interface',
    title: 'Interfaz de Evaluación',
    content: 'Esta es la interfaz principal de evaluación. Aquí puedes ver las habilidades de la dimensión seleccionada y evaluar a tus estudiantes.',
    targetSelector: '.bg-white, .dimension-categories, main',
    position: 'top',
    action: 'highlight',
    expectedSection: 'formularios'
  },
  {
    id: 'select-student-view',
    title: 'Vista por Estudiante',
    content: 'En la Vista por Estudiante, seleccionas uno o más estudiantes y evalúas todas sus habilidades en esta dimensión. El tutorial cambiará automáticamente a este modo.',
    targetSelector: '.bg-white, .dimension-categories, main',
    position: 'bottom',
    action: 'view-mode-change',
    actionTarget: 'student',
    expectedSection: 'formularios'
  },
  {
    id: 'evaluation-area',
    title: 'Área de Evaluación',
    content: 'Esta es el área principal donde aparecen las habilidades una vez que seleccionas estudiantes. Para cada habilidad, puedes seleccionar el nivel de logro observado del estudiante.',
    targetSelector: '.bg-white, .dimension-categories, main',
    position: 'center',
    action: 'highlight',
    expectedSection: 'formularios'
  },
  {
    id: 'save-system-explanation',
    title: 'Sistema de Guardado por Lotes',
    content: '¡Importante! Las evaluaciones se consolidan mientras evalúas y se envían a la base de datos cuando decides guardar. Esto te permite evaluar varios estudiantes y luego guardar todo por lotes.',
    targetSelector: '.bg-white, .dimension-categories, main',
    position: 'center',
    action: 'highlight',
    expectedSection: 'formularios'
  },
  {
    id: 'switch-to-skill-view',
    title: 'Vista por Habilidad',
    content: 'Ahora cambiemos a la Vista por Habilidad. En este modo, seleccionas una habilidad específica y evalúas a todos los estudiantes en esa habilidad particular. El tutorial cambiará automáticamente.',
    targetSelector: '.bg-white, .dimension-categories, main',
    position: 'bottom',
    action: 'view-mode-change',
    actionTarget: 'skill',
    expectedSection: 'formularios'
  },
  {
    id: 'skill-evaluation-interface',
    title: 'Interfaz de Evaluación por Habilidad',
    content: 'En la Vista por Habilidad, primero seleccionas una habilidad específica, luego puedes evaluar a todos los estudiantes en esa habilidad de manera eficiente.',
    targetSelector: '.bg-white, .dimension-categories, main',
    position: 'center',
    action: 'highlight',
    expectedSection: 'formularios'
  },
  {
    id: 'save-progress-note',
    title: 'Guardar las Evaluaciones',
    content: 'Recuerda: después de evaluar a los estudiantes, debes hacer clic en "Guardar" para enviar todas las evaluaciones a la base de datos por lotes. Esto asegura que todos los datos se almacenen correctamente.',
    targetSelector: '.bg-white, .dimension-categories, main',
    position: 'center',
    action: 'highlight',
    expectedSection: 'formularios'
  },

  // === TRANSICIÓN A ANALYTICS ===
  {
    id: 'analytics-access',
    title: 'Vamos a Analytics',
    content: 'Una vez que tengas evaluaciones, Analytics te permite ver resultados, gráficos y análisis del progreso de tus estudiantes.',
    targetSelector: '[data-section="analytics"]',
    position: 'bottom',
    action: 'click',
    actionTarget: 'analytics',
    expectedSection: 'formularios'
  },
  {
    id: 'analytics-overview',
    title: 'Vista de Analytics',
    content: 'En Analytics puedes ver gráficos de progreso, comparativas por curso, análisis de dimensiones y reportes detallados de cada estudiante.',
    targetSelector: '.analytics-content',
    position: 'top',
    action: 'highlight',
    expectedSection: 'analytics'
  },

  // === SECCIÓN EXPORTACIÓN EXPANDIDA ===
  {
    id: 'export-access',
    title: 'Exportación de Informes',
    content: 'La sección de Exportación te permite generar informes PDF individualizados y exportar todos los datos a Excel para análisis externos.',
    targetSelector: '[data-section="exportacion"]',
    position: 'bottom',
    action: 'click',
    actionTarget: 'exportacion',
    expectedSection: 'analytics'
  },
  {
    id: 'export-options-overview',
    title: 'Opciones de Exportación',
    content: 'Aquí tienes dos opciones principales: generar informes PDF por estudiante individual o exportar todos los datos a Excel.',
    targetSelector: '.export-options',
    position: 'top',
    action: 'highlight',
    expectedSection: 'exportacion'
  },
  {
    id: 'students-list-export',
    title: 'Lista de Estudiantes',
    content: 'En esta sección puedes ver todos los estudiantes disponibles para generar informes PDF individuales.',
    targetSelector: '.grid.grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-3.xl\\:grid-cols-4.gap-4',
    position: 'left',
    action: 'highlight',
    expectedSection: 'exportacion'
  },
  {
    id: 'select-student-for-pdf',
    title: 'Icono de Vista Previa',
    content: 'Para ver la vista previa de un estudiante, debes hacer clic en el icono del ojo que aparece en su tarjeta. El tutorial hará clic automáticamente en este icono.',
    targetSelector: '.grid.grid-cols-1.sm\\:grid-cols-2 div:first-child button[title*="Vista"], .grid button[title="Vista previa del informe"]:first-of-type, .grid > div:first-child .fa-eye',
    position: 'right',
    action: 'student-select',
    actionTarget: 'first-student-eye-icon',
    expectedSection: 'exportacion'
  },
  {
    id: 'student-individual-view',
    title: 'Vista Previa del Informe',
    content: '¡Perfecto! Esta es la vista previa completa del informe que se generará en el PDF. Incluye información académica, estadísticas y resumen por categorías.',
    targetSelector: '.fixed.inset-0.bg-black.bg-opacity-50 .bg-white.rounded-xl, .fixed.inset-0 .bg-white.rounded-xl',
    position: 'center',
    action: 'highlight',
    expectedSection: 'exportacion'
  },
  {
    id: 'generate-pdf-button',
    title: 'Botón Exportar PDF',
    content: 'Este es el botón "Exportar PDF" que genera el informe completo del estudiante. Está ubicado en la parte superior derecha del modal de vista previa.',
    targetSelector: '.fixed.inset-0 .sticky.top-0 button.bg-blue-600, .fixed .bg-white .sticky button.bg-blue-600, .fixed .rounded-xl .sticky button:last-child',
    position: 'top',
    action: 'highlight',
    expectedSection: 'exportacion'
  },

  // === FINALIZACIÓN ===
  {
    id: 'completion',
    title: '¡Tutorial Completado!',
    content: '¡Excelente! Ya dominas todas las funciones principales: evaluación por estudiante y habilidad, guardado automático, análisis en Analytics y generación de informes PDF. ¡Ahora puedes usar la plataforma con total confianza!',
    position: 'center',
    expectedSection: 'exportacion'
  }
];

const TutorialFixed: React.FC<TutorialProps> = ({ 
  isOpen, 
  onClose, 
  onComplete, 
  currentSection,
  onSectionChange,
  onDimensionSelect,
  onViewModeChange
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hasAutoNavigated, setHasAutoNavigated] = useState(false);

  const step = tutorialSteps[currentStep];

  // Gestión de estado simplificada
  const getTutorialState = (): TutorialState | null => {
    const saved = localStorage.getItem('tutorialState');
    if (!saved) return null;
    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  };

  const saveTutorialState = (stepNum: number, section: TutorialSection) => {
    const state: TutorialState = {
      isActive: true,
      currentStep: stepNum,
      currentSection: section
    };
    localStorage.setItem('tutorialState', JSON.stringify(state));
  };

  // Posicionamiento del modal
  const highlightElement = useCallback((selector: string) => {
    const element = selector ? document.querySelector(selector) as HTMLElement : null;
    
    // Debug log para verificar qué elemento se está encontrando
    if (selector && !element) {
      console.warn(`🎯 Tutorial: No se encontró el elemento con selector "${selector}"`);
    } else if (element) {
      const rect = element.getBoundingClientRect();
      console.log(`🎯 Tutorial: Elemento encontrado "${selector}":`, {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left
      });
    }
    
    setHighlightedElement(element);
    
    const header = document.querySelector('nav.bg-gradient-to-r.from-slate-800.to-slate-700') ||
                  document.querySelector('nav.fixed.top-0') ||
                  document.querySelector('nav');
    
    const baseHeaderHeight = 64;
    let effectiveHeaderHeight = baseHeaderHeight;
    
    if (header) {
      const headerRect = header.getBoundingClientRect();
      effectiveHeaderHeight = headerRect.height > 0 ? headerRect.height : baseHeaderHeight;
    }

    // Usar la misma lógica para todas las secciones - sin lógica especial para dashboard
    // que causaba problemas de posicionamiento
    
    const availableHeight = window.innerHeight - effectiveHeaderHeight;
    const availableTop = effectiveHeaderHeight;
    
    const tooltipWidth = Math.min(450, window.innerWidth - 40);
    const tooltipHeight = Math.min(400, availableHeight * 0.9);
    
    let x = 0, y = 0;
    
    if (element && step.position !== 'center') {
      const rect = element.getBoundingClientRect();
      
      switch (step.position) {
        case 'top':
          x = rect.left + (rect.width / 2) - (tooltipWidth / 2);
          y = Math.max(availableTop + 20, rect.top - tooltipHeight - 20);
          break;
        case 'bottom':
          x = rect.left + (rect.width / 2) - (tooltipWidth / 2);
          y = Math.min(window.innerHeight - tooltipHeight - 20, rect.bottom + 20);
          break;
        case 'left':
          x = Math.max(20, rect.left - tooltipWidth - 20);
          y = Math.max(availableTop + 20, rect.top + (rect.height / 2) - (tooltipHeight / 2));
          break;
        case 'right':
          x = Math.min(window.innerWidth - tooltipWidth - 20, rect.right + 20);
          y = Math.max(availableTop + 20, rect.top + (rect.height / 2) - (tooltipHeight / 2));
          break;
      }
    } else {
      // Para posición center, también usar el availableTop correcto
      x = (window.innerWidth / 2) - (tooltipWidth / 2);
      y = availableTop + (availableHeight / 2) - (tooltipHeight / 2);
    }
    
    x = Math.max(20, Math.min(x, window.innerWidth - tooltipWidth - 20));
    y = Math.max(availableTop + 20, Math.min(y, window.innerHeight - tooltipHeight - 20));
    
    setTooltipPosition({ x, y });

    if (element && step.position !== 'center') {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'center'
      });
    }
  }, [step, currentSection, isOpen, onSectionChange, onDimensionSelect, onViewModeChange, currentStep]);

  // Ejecutar acciones del paso
  const executeStepAction = useCallback((forceNavigation = false) => {
    if (!step || !isOpen) return;

    if (currentSection !== step.expectedSection) {
      return;
    }

    switch (step.action) {
      case 'highlight':
        setTimeout(() => highlightElement(step.targetSelector || ''), 300);
        break;

      case 'click':
        // Solo navegar si se hace clic en "Siguiente"
        if (step.actionTarget && onSectionChange && forceNavigation) {
          console.log('🧭 Navegando a', step.actionTarget, '(manual)');
          onSectionChange(step.actionTarget as TutorialSection);
          saveTutorialState(currentStep, step.actionTarget as TutorialSection);
          setHasAutoNavigated(true);
          setTimeout(() => highlightElement(step.targetSelector || ''), 1500);
        } else {
          // Solo resaltar el elemento sin navegar
          setTimeout(() => highlightElement(step.targetSelector || ''), 300);
        }
        break;

      case 'dimension-select':
        // Seleccionar una dimensión específica - SIEMPRE ejecutar si forceNavigation es true
        if (step.actionTarget && onDimensionSelect && forceNavigation) {
          console.log('🎯 Seleccionando dimensión:', step.actionTarget, 'onDimensionSelect disponible:', !!onDimensionSelect, 'forceNavigation:', forceNavigation);
          onDimensionSelect(step.actionTarget as number);
          setTimeout(() => highlightElement(step.targetSelector || ''), 1000);
        } else {
          console.warn('❌ No se puede seleccionar dimensión:', {
            actionTarget: step.actionTarget,
            onDimensionSelect: !!onDimensionSelect,
            forceNavigation: forceNavigation
          });
          setTimeout(() => highlightElement(step.targetSelector || ''), 300);
        }
        break;

      case 'view-mode-change':
        // Cambiar modo de vista (estudiante/habilidad) - SIEMPRE ejecutar si forceNavigation es true
        if (step.actionTarget && onViewModeChange && forceNavigation) {
          console.log('👁️ Cambiando vista a:', step.actionTarget, 'onViewModeChange disponible:', !!onViewModeChange, 'forceNavigation:', forceNavigation);
          onViewModeChange(step.actionTarget as 'student' | 'skill');
          setTimeout(() => highlightElement(step.targetSelector || ''), 800);
        } else {
          console.warn('❌ No se puede cambiar vista:', {
            actionTarget: step.actionTarget,
            onViewModeChange: !!onViewModeChange,
            forceNavigation: forceNavigation
          });
          setTimeout(() => highlightElement(step.targetSelector || ''), 300);
        }
        break;

      case 'student-select':
        // Seleccionar un estudiante específico (para exportación) - buscar el icono del ojo
        if (step.actionTarget && forceNavigation) {
          console.log('👁️ Seleccionando icono de vista previa automáticamente, forceNavigation:', forceNavigation);
          setTimeout(() => {
            // Primero intentar los selectores específicos del paso
            let eyeElement = null;
            const selectors = step.targetSelector?.split(', ') || [];
            
            for (const selector of selectors) {
              try {
                eyeElement = document.querySelector(selector.trim()) as HTMLElement;
                if (eyeElement) {
                  console.log('👁️ Encontrado icono del ojo con selector:', selector.trim());
                  break;
                }
              } catch (e) {
                console.warn('⚠️ Selector inválido:', selector.trim(), e);
              }
            }
            
            // Si no se encuentra, buscar alternativas comunes
            if (!eyeElement) {
              const fallbackSelectors = [
                'button[title="Vista previa del informe"]',
                '[title*="Vista"]',
                '[title*="Previa"]',
                '.fa-eye',
                '.eye-icon',
                '.preview-btn',
                'button[onclick*="preview"]',
                '.grid > div:first-child button:last-child'
              ];
              
              for (const selector of fallbackSelectors) {
                try {
                  eyeElement = document.querySelector(selector) as HTMLElement;
                  if (eyeElement) {
                    console.log('👁️ Encontrado icono del ojo con fallback:', selector);
                    break;
                  }
                } catch (e) {
                  console.warn('⚠️ Selector fallback inválido:', selector, e);
                }
              }
            }
            
            if (eyeElement) {
              console.log('👁️ Haciendo click en icono del ojo:', eyeElement);
              eyeElement.click();
              // Esperar más tiempo para que el modal se abra completamente
              setTimeout(() => highlightElement(step.targetSelector || ''), 2000);
            } else {
              console.warn('❌ No se encontró icono del ojo para hacer click');
              setTimeout(() => highlightElement(step.targetSelector || ''), 300);
            }
          }, 500);
        } else {
          setTimeout(() => highlightElement(step.targetSelector || ''), 300);
        }
        break;

      default:
        if (step.position === 'center') {
          setTimeout(() => highlightElement(''), 300);
        } else {
          setTimeout(() => highlightElement(step.targetSelector || ''), 300);
        }
    }
  }, [step, currentStep, currentSection, isOpen, onSectionChange, onDimensionSelect, onViewModeChange]);

  // Cargar estado al abrir
  useEffect(() => {
    if (isOpen) {
      setHasAutoNavigated(false); // Reset al abrir
      const savedState = getTutorialState();
      if (savedState) {
        setCurrentStep(savedState.currentStep);
        if (currentSection !== savedState.currentSection && onSectionChange) {
          onSectionChange(savedState.currentSection);
        }
      }
      setTimeout(() => highlightElement(step?.targetSelector || ''), 100);
    }
  }, [isOpen]);

  // Posicionar modal al cambiar paso
  useEffect(() => {
    if (isOpen && step) {
      setTimeout(() => {
        highlightElement(step.targetSelector || '');
      }, 100);
    }
  }, [isOpen, currentStep, highlightElement, step]);

  // Ejecutar acciones cuando cambia el paso o la sección
  useEffect(() => {
    if (isOpen && step && currentSection === step.expectedSection) {
      const timer = setTimeout(() => {
        executeStepAction(false); // No forzar navegación al cambiar pasos
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen, currentStep, currentSection, step, executeStepAction]);



  // Navegación
  const nextStep = useCallback(() => {
    // Si el paso actual requiere una acción especial, ejecutarla
    if (step && currentSection === step.expectedSection) {
      if (step.action === 'click' && step.actionTarget && !hasAutoNavigated && step.actionTarget !== currentSection) {
        executeStepAction(true); // Forzar navegación al hacer click en "Siguiente"
        return; // No avanzar el paso aún, esperar a que se complete la navegación
      }
      
      if (step.action === 'dimension-select' || step.action === 'view-mode-change' || step.action === 'student-select') {
        executeStepAction(true); // Ejecutar acción al hacer click en "Siguiente"
        // Continuar al siguiente paso inmediatamente para estas acciones
      }
    }

    // Avanzar al siguiente paso
    if (currentStep < tutorialSteps.length - 1) {
      const newStep = currentStep + 1;
      const newStepData = tutorialSteps[newStep];
      
      setCurrentStep(newStep);
      setHasAutoNavigated(false); // Reset para el nuevo paso
      
      if (newStepData.expectedSection !== currentSection && onSectionChange) {
        onSectionChange(newStepData.expectedSection);
      }
      
      saveTutorialState(newStep, newStepData.expectedSection);
    } else {
      completeTutorial();
    }
  }, [currentStep, currentSection, onSectionChange, step, hasAutoNavigated, executeStepAction]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      const newStepData = tutorialSteps[newStep];
      
      setCurrentStep(newStep);
      setHasAutoNavigated(false); // Reset para el nuevo paso
      
      if (newStepData.expectedSection !== currentSection && onSectionChange) {
        onSectionChange(newStepData.expectedSection);
      }
      
      saveTutorialState(newStep, newStepData.expectedSection);
    }
  }, [currentStep, currentSection, onSectionChange]);

  const closeTutorial = useCallback(() => {
    setHighlightedElement(null);
    localStorage.removeItem('tutorialState');
    onClose();
  }, [onClose]);

  const completeTutorial = useCallback(() => {
    localStorage.setItem('tutorialCompleted', 'true');
    localStorage.removeItem('tutorialState');
    onComplete?.();
    onClose();
  }, [onComplete, onClose]);

  if (!isOpen) return null;

  const stepIcon = stepIcons[step.id] || stepIcons['welcome'];

  return (
    <div className="fixed inset-0" style={{ zIndex: 9998 }}>
      {/* Overlay Mejorado */}
      <div 
        className="absolute inset-0 transition-all duration-500"
        style={{
          background: highlightedElement 
            ? (() => {
                const rect = highlightedElement.getBoundingClientRect();
                // Solo aplicar highlight si el elemento tiene dimensiones válidas
                if (rect.width > 0 && rect.height > 0) {
                  const centerX = rect.left + rect.width/2;
                  const centerY = rect.top + rect.height/2;
                  // Limitar el área de highlight para evitar elementos gigantes
                  const maxSize = Math.min(Math.max(rect.width, rect.height), 300); // Reducido a 300px
                  const transparentRadius = Math.max(maxSize/2 + 30, 60); // Mínimo 60px, margen 30px
                  const darkRadius = transparentRadius + 60; // Transición más suave
                  return `radial-gradient(circle at ${centerX}px ${centerY}px, transparent ${transparentRadius}px, rgba(0,0,0,0.7) ${darkRadius}px)`;
                }
                return 'rgba(0,0,0,0.7)';
              })()
            : 'rgba(0,0,0,0.7)',
          zIndex: 9998
        }}
      />
      
      {/* Highlight ring mejorado */}
      {highlightedElement && (
        <>
          {/* Anillo exterior brillante con tamaño limitado */}
          <div
            className="absolute pointer-events-none rounded-lg border-4 border-blue-400 shadow-2xl tutorial-highlight-ring"
            style={(() => {
              const rect = highlightedElement.getBoundingClientRect();
              // Solo mostrar anillo si el elemento tiene dimensiones válidas y no es demasiado grande
              if (rect.width > 0 && rect.height > 0) {
                // Limitar el tamaño del anillo para elementos muy grandes
                const maxWidth = Math.min(rect.width, 400);  // Reducido a 400px de ancho
                const maxHeight = Math.min(rect.height, 250); // Reducido a 250px de alto
                
                // Centrar el anillo limitado en el elemento original
                const centerX = rect.left + rect.width/2;
                const centerY = rect.top + rect.height/2;
                const ringLeft = centerX - maxWidth/2 - 8;
                const ringTop = centerY - maxHeight/2 - 8;
                
                return {
                  left: ringLeft,
                  top: ringTop,
                  width: maxWidth + 16,
                  height: maxHeight + 16,
                  zIndex: 9999,
                  boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 15px rgba(59, 130, 246, 0.8), 0 0 30px rgba(59, 130, 246, 0.5)',
                  background: 'rgba(59, 130, 246, 0.15)',
                  border: '2px solid rgba(59, 130, 246, 0.8)'
                };
              }
              return { display: 'none' };
            })()}
          />
          
          {/* Fondo semi-transparente para destacar */}
          <div
            className="absolute pointer-events-none rounded-lg bg-white bg-opacity-20"
            style={(() => {
              const rect = highlightedElement.getBoundingClientRect();
              // Solo mostrar fondo si el elemento tiene dimensiones válidas
              if (rect.width > 0 && rect.height > 0) {
                // Limitar el tamaño del fondo para elementos muy grandes
                const maxWidth = Math.min(rect.width, 400);
                const maxHeight = Math.min(rect.height, 250);
                
                const centerX = rect.left + rect.width/2;
                const centerY = rect.top + rect.height/2;
                const bgLeft = centerX - maxWidth/2 - 4;
                const bgTop = centerY - maxHeight/2 - 4;
                
                return {
                  left: bgLeft,
                  top: bgTop,
                  width: maxWidth + 8,
                  height: maxHeight + 8,
                  zIndex: 9998
                };
              }
              return { display: 'none' };
            })()}
          />
        </>
      )}

      {/* Tutorial Modal */}
      <div
        className="absolute bg-white rounded-xl shadow-2xl border border-gray-200"
        style={{
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          width: Math.min(450, window.innerWidth - 40) + 'px',
          maxHeight: Math.min(500, (window.innerHeight - 64) * 0.9) + 'px',
          overflow: 'auto',
          zIndex: 9999
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {stepIcon}
            <h2 className="text-lg font-bold text-gray-900">{step.title}</h2>
          </div>
          <button onClick={closeTutorial} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 leading-relaxed mb-4">{step.content}</p>
          
          {/* Instrucción especial para pasos de navegación en modo manual */}
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-500 mb-2">
              <span>Progreso</span>
              <span>{currentStep + 1} de {tutorialSteps.length}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / tutorialSteps.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-600">
              Paso {currentStep + 1} de {tutorialSteps.length}
            </span>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center space-x-1 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FaArrowLeft />
              <span>Anterior</span>
            </button>
            
            <button
              onClick={nextStep}
              className="flex items-center space-x-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm"
            >
              <span>{currentStep === tutorialSteps.length - 1 ? 'Finalizar' : 'Siguiente'}</span>
              <FaArrowRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialFixed;
